import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Modal,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@/components/firebase/useFirebase";
import { reportInappropriateContent } from "@/services/firebase/messaging-functions";
import { IReportInappropriateContentResponse } from "common247";

/**
 * Default categories for reporting inappropriate or violating content.
 */
export const REPORT_REASONS: readonly string[] = [
  "Inappropriate Media",
  "Harassment / Abuse",
  "Violence / Harmful",
  "Spam / Phishing",
  "Copyright Violation",
  "Other",
] as const;

/**
 * Props for the InappropriateContentModal component.
 */
export interface IInappropriateContentModalProps {
  /** Controls modal visibility. */
  visible: boolean;
  /** Callback fired when modal is closed or cancelled. */
  onClose: () => void;
  /** Optional callback fired when a report has been successfully submitted. */
  onSubmitted?: (response: IReportInappropriateContentResponse) => void;
  /** Optional pre-filled reason/category. Defaults to "Inappropriate Media". */
  initialReason?: string;
  /** Optional pre-filled content reference or identifier. */
  initialContentUrl?: string;
}

/**
 * Modal dialog component for reporting inappropriate content, harmful media, or platform violations.
 * Automatically handles validation, submission via reportInappropriateContent, and alerts.
 */
export function InappropriateContentModal({
  visible,
  onClose,
  onSubmitted,
  initialReason = "Inappropriate Media",
  initialContentUrl = "",
}: IInappropriateContentModalProps): React.JSX.Element {
  const services = useFirebase();

  const cardBgColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const modalOverlayColor = "rgba(0,0,0,0.5)";

  const [reportReason, setReportReason] = useState<string>(initialReason);
  const [reportUrl, setReportUrl] = useState<string>(initialContentUrl);
  const [reportDescription, setReportDescription] = useState<string>("");
  const [reportDescriptionError, setReportDescriptionError] = useState<string>("");
  const [submittingReport, setSubmittingReport] = useState<boolean>(false);

  // Sync initial props when modal becomes visible
  useEffect(() => {
    if (visible) {
      setReportReason(initialReason);
      setReportUrl(initialContentUrl);
      setReportDescription("");
      setReportDescriptionError("");
      setSubmittingReport(false);
    }
  }, [visible, initialReason, initialContentUrl]);

  const handleClose = () => {
    setReportReason(initialReason);
    setReportUrl(initialContentUrl);
    setReportDescription("");
    setReportDescriptionError("");
    setSubmittingReport(false);
    onClose();
  };

  const handleReportSubmit = async () => {
    setReportDescriptionError("");

    const trimmedDesc = reportDescription.trim();
    if (!trimmedDesc) {
      setReportDescriptionError("Please describe what content is inappropriate and why.");
      return;
    }

    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    setSubmittingReport(true);
    try {
      const response = await reportInappropriateContent(services, {
        reason: reportReason,
        description: trimmedDesc,
        contentUrl: reportUrl.trim() || undefined,
      });

      if (response && response.status) {
        handleClose();
        CustomAlert.alert(
          "Report Submitted",
          "Your report has been sent to system administrators for review."
        );
        if (onSubmitted) {
          onSubmitted(response);
        }
      } else {
        CustomAlert.alert(
          "Report Failed",
          response?.error || "Failed to submit report. Please try again."
        );
        setSubmittingReport(false);
      }
    } catch (err: any) {
      console.error("Error submitting inappropriate content report:", err);
      CustomAlert.alert("Error", err.message || "Failed to submit report.");
      setSubmittingReport(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleClose}
      testID="inappropriate-content-modal"
    >
      <View style={[styles.modalOverlay, { backgroundColor: modalOverlayColor }]}>
        <ThemedView
          style={[
            styles.modalContent,
            styles.reportModalContent,
            { backgroundColor: cardBgColor, borderColor: borderColor, borderWidth: 1 },
          ]}
        >
          <ScrollView
            contentContainerStyle={styles.reportScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <ThemedText type="subtitle" style={styles.modalTitle}>
              Report Inappropriate Content
            </ThemedText>

            <ThemedText style={styles.reportHelpText}>
              Reports are sent directly to all administrators as an alert for review.
            </ThemedText>

            {/* Category / Reason Selection */}
            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">Reason / Category</ThemedText>
              <View style={styles.reasonChipsContainer}>
                {REPORT_REASONS.map((r) => {
                  const isSelected = reportReason === r;
                  return (
                    <TouchableOpacity
                      key={r}
                      style={[
                        styles.reasonChip,
                        { borderColor: isSelected ? tintColor : borderColor },
                        isSelected && { backgroundColor: tintColor },
                      ]}
                      onPress={() => setReportReason(r)}
                      accessibilityRole="button"
                      accessibilityState={{ selected: isSelected }}
                    >
                      <ThemedText
                        style={[
                          styles.reasonChipText,
                          isSelected && { color: "#FFFFFF", fontWeight: "700" },
                        ]}
                      >
                        {r}
                      </ThemedText>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* Optional Content Link / Title / Identifier */}
            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">
                Content Link, Title, or ID (Optional)
              </ThemedText>
              <ThemedInput
                value={reportUrl}
                onChangeText={setReportUrl}
                placeholder="e.g. Memory title, media link, or user"
              />
            </View>

            {/* Required Details / Description */}
            <View style={styles.inputWrapper}>
              <ThemedText type="fieldLabel">
                Details / Description (Required)
              </ThemedText>
              <ThemedInput
                value={reportDescription}
                onChangeText={(val) => {
                  setReportDescription(val);
                  if (reportDescriptionError) setReportDescriptionError("");
                }}
                multiline={true}
                numberOfLines={4}
                placeholder="Please describe why this content is inappropriate..."
                error={reportDescriptionError}
                style={styles.multilineInput}
              />
            </View>

            {submittingReport ? (
              <ActivityIndicator size="small" color={tintColor} style={styles.modalLoader} />
            ) : (
              <View style={styles.modalBtnRow}>
                <ThemedButton
                  title="Cancel"
                  variant="minimal"
                  onPress={handleClose}
                />

                <TouchableOpacity
                  style={[styles.modalBtn, styles.submitBtn, { backgroundColor: tintColor }]}
                  onPress={handleReportSubmit}
                  accessibilityRole="button"
                  accessibilityLabel="Submit Report"
                >
                  <ThemedText style={styles.submitBtnText}>Submit Report</ThemedText>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </ThemedView>
      </View>
    </Modal>
  );
}

// Aliases matching alternative naming preferences
export const InappropriateContentDialogModal = InappropriateContentModal;
export default InappropriateContentModal;

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    borderRadius: 0,
    width: "88%",
    maxWidth: 420,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  reportModalContent: {
    maxHeight: "90%",
    width: "92%",
    maxWidth: 480,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  reportScrollContent: {
    paddingBottom: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 6,
    textAlign: "center",
  },
  reportHelpText: {
    fontSize: 12,
    opacity: 0.7,
    marginBottom: 12,
    lineHeight: 16,
    textAlign: "center",
  },
  inputWrapper: {
    marginBottom: 12,
    gap: 4,
  },
  reasonChipsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 4,
  },
  reasonChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 0,
    borderWidth: 1,
  },
  reasonChipText: {
    fontSize: 11,
  },
  multilineInput: {
    minHeight: 80,
    textAlignVertical: "top",
    paddingTop: 8,
  },
  modalLoader: {
    marginVertical: 12,
    alignSelf: "center",
  },
  modalBtnRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
    alignItems: "center",
  },
  modalBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  submitBtn: {
    minWidth: 110,
  },
  submitBtnText: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 14,
  },
});
